import { CSRF_HEADER, type ProjectDetail } from "@esi/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "../../api.ts";
import { apiError } from "../../api-errors.ts";
import { lastProject } from "../../home/last-project.ts";
import { navigate, PROJECTS_PATH } from "../../router.tsx";
import { useSession } from "../../session.tsx";
import { projectKeys } from "../queries.ts";
import { projectRefusal } from "./refusals.ts";

/** Deleting the project (the owner only, `project.delete`), after typing its exact name. */
export function DangerZone({ project, frozen }: { project: ProjectDetail; frozen: boolean }) {
  const { t } = useTranslation();
  const id = useId();
  const { csrfToken } = useSession();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  // Every way out clears the typed name, so reopening never starts armed.
  const close = () => {
    // A delete in flight cannot be called back: no way out until it settles.
    if (remove.isPending) {
      return;
    }
    setOpen(false);
    setTyped("");
    remove.reset();
  };
  const remove = useMutation({
    mutationFn: async () => {
      const res = await api.api.projects[":projectId"].$delete(
        { param: { projectId: project.id } },
        { headers: { [CSRF_HEADER]: csrfToken } },
      );
      if (!res.ok) {
        throw await apiError(res);
      }
    },
    onSuccess: () => {
      // Gone everywhere: its queries, recent projects, and home's preselection.
      queryClient.removeQueries({ queryKey: projectKeys.project(project.id) });
      void queryClient.invalidateQueries({ queryKey: projectKeys.all });
      lastProject.forget(project.id);
      toast(t("settings.danger.deleted", { name: project.name }));
      navigate(PROJECTS_PATH);
    },
  });
  return (
    <div
      data-esi-content="projects"
      className="flex flex-col gap-3 rounded-lg border border-danger-border/50 p-4"
    >
      <p className="text-sm text-fg-secondary">{t("settings.danger.lead")}</p>
      <Button
        variant="destructive"
        className="self-start"
        disabled={frozen}
        onClick={() => setOpen(true)}
      >
        {t("settings.danger.delete")}
      </Button>
      <AppDialog
        open={open}
        onClose={close}
        title={t("settings.danger.title", { name: project.name })}
        description={t("settings.danger.body")}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (typed === project.name && !remove.isPending) {
              remove.mutate();
            }
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${id}-name`}>
              {t("settings.danger.typeName", { name: project.name })}
            </Label>
            <Input
              id={`${id}-name`}
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.currentTarget.value)}
            />
          </div>
          {remove.isError ? <Notice tone="danger">{projectRefusal(remove.error)}</Notice> : null}
          <DialogFooter>
            <Button type="button" variant="outline" disabled={remove.isPending} onClick={close}>
              {t("settings.cancel")}
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={typed !== project.name || remove.isPending}
            >
              {remove.isPending ? t("settings.danger.deleting") : t("settings.danger.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </AppDialog>
    </div>
  );
}
